import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';

import {
  DocumentCreateRequest,
  DocumentUpdateRequest,
  SimpleCatalogOption,
  SwapActionItem,
  SwapActionTypeOption,
  SwapActionUpsertRequest,
  SwapDetail,
  SwapDetailSaveRequest,
  SwapDocumentItem,
  SwapExtLocalDetail,
  SwapFeeItem,
  SwapFeeTypeOption,
  SwapFeeUpsertRequest,
  SwapFormulaItem,
  SwapLegDetail,
  SwapNoteItem,
  SwapNoteUpsertRequest,
  SwapPurposeSubtypeOption,
} from './swap-detail.types';
import { apiUrl } from '../../../auth/api-url';

@Injectable({
  providedIn: 'root',
})
export class SwapDetailService {
  private readonly endpoint = apiUrl('Swap');
  private readonly documentsEndpoint = apiUrl('document');
  private readonly feeEndpoint = apiUrl('SwapFee');
  private readonly feeTypeEndpoint = apiUrl('SwapFeeType');
  private readonly actionEndpoint = apiUrl('SwapAction');
  private readonly actionTypeEndpoint = apiUrl('SwapActionType');
  private readonly notesEndpoint = apiUrl('Notes');

  constructor(private readonly http: HttpClient) {}

  getSwapDetail(tradeId: string): Observable<SwapDetail> {
    return this.http.get<unknown>(`${this.endpoint}/${encodeURIComponent(tradeId)}`).pipe(
      map((response) => this.normalizeSwapDetail(response)),
    );
  }

  saveSwapDetail(payload: SwapDetailSaveRequest): Observable<void> {
    return this.http.post<void>(`${this.endpoint}/details`, payload);
  }

  createSwapFee(payload: SwapFeeUpsertRequest): Observable<SwapFeeItem> {
    return this.http.post<unknown>(this.feeEndpoint, payload).pipe(map((response) => this.toSwapFee(response)));
  }

  updateSwapFee(id: number, payload: SwapFeeUpsertRequest): Observable<void> {
    return this.http.put<void>(`${this.feeEndpoint}/${id}`, payload);
  }

  deleteSwapFee(id: number): Observable<void> {
    return this.http.delete<void>(`${this.feeEndpoint}/${id}`);
  }

  getSwapFeeTypes(): Observable<SwapFeeTypeOption[]> {
    return this.http.get<unknown>(this.feeTypeEndpoint).pipe(map((response) => this.toSwapFeeTypes(response)));
  }

  getSwapActionTypes(): Observable<SwapActionTypeOption[]> {
    return this.http.get<unknown>(this.actionTypeEndpoint).pipe(map((response) => this.toSwapActionTypes(response)));
  }

  createSwapAction(payload: SwapActionUpsertRequest): Observable<SwapActionItem> {
    return this.http.post<unknown>(this.actionEndpoint, payload).pipe(map((response) => this.toSwapAction(response)));
  }

  updateSwapAction(id: number, payload: SwapActionUpsertRequest): Observable<void> {
    return this.http.put<void>(`${this.actionEndpoint}/${id}`, payload);
  }

  deleteSwapAction(id: number): Observable<void> {
    return this.http.delete<void>(`${this.actionEndpoint}/${id}`);
  }

  getNotes(): Observable<SwapNoteItem[]> {
    return this.http.get<unknown>(this.notesEndpoint).pipe(map((response) => this.normalizeNotes(response)));
  }

  createNote(payload: SwapNoteUpsertRequest): Observable<SwapNoteItem> {
    return this.http.post<unknown>(this.notesEndpoint, payload).pipe(map((response) => this.normalizeNote(response)));
  }

  updateNote(id: number, payload: SwapNoteUpsertRequest): Observable<void> {
    return this.http.put<void>(`${this.notesEndpoint}/${encodeURIComponent(String(id))}`, payload);
  }

  deleteNote(id: number): Observable<void> {
    return this.http.delete<void>(`${this.notesEndpoint}/${encodeURIComponent(String(id))}`);
  }

  getDocumentsByTrade(tradeId: string): Observable<SwapDocumentItem[]> {
    return this.http.get<unknown>(`${this.documentsEndpoint}/trade/${encodeURIComponent(tradeId)}`).pipe(
      map((response) => this.normalizeDocuments(response)),
    );
  }

  createDocument(payload: DocumentCreateRequest): Observable<SwapDocumentItem> {
    const formData = new FormData();
    formData.append('Filename', payload.filename);
    if (payload.parentElementId) {
      formData.append('ParentElementId', payload.parentElementId);
    }
    if (payload.securityId) {
      formData.append('SecurityId', payload.securityId);
    }
    formData.append('Description', payload.description ?? '');
    formData.append('Type', payload.type);
    formData.append('Source', payload.source);
    formData.append('ModifiedBy', payload.modifiedBy);
    formData.append('File', payload.file);

    return this.http.post<unknown>(this.documentsEndpoint, formData).pipe(
      map((response) => this.toDocument(this.extractResponseRecord(response))),
    );
  }

  updateDocument(id: number, payload: DocumentUpdateRequest): Observable<SwapDocumentItem> {
    return this.http.put<unknown>(
      `${this.documentsEndpoint}/${encodeURIComponent(String(id))}`,
      payload,
    ).pipe(
      map((response) => this.toDocument(this.extractResponseRecord(response))),
    );
  }

  deleteDocument(id: number): Observable<void> {
    return this.http.delete<void>(`${this.documentsEndpoint}/${encodeURIComponent(String(id))}`);
  }

  private normalizeSwapDetail(response: unknown): SwapDetail {
    const record = this.toRecord(response);

    return {
      pay: this.toSwapLeg(this.read(record, ['pay', 'Pay'])),
      receive: this.toSwapLeg(this.read(record, ['receive', 'Receive'])),
      swapExtLocal: this.toSwapExtLocal(this.read(record, ['swapExtLocal', 'SwapExtLocal'])),
      swapActions: this.toSwapActions(this.read(record, ['swapActions', 'SwapActions'])),
      swapFees: this.toSwapFees(this.read(record, ['swapFees', 'SwapFees'])),
      documentsAttached: this.toDocuments(this.read(record, ['documentsAttached', 'DocumentsAttached'])),
      notes: this.toNotes(this.read(record, ['notes', 'Notes'])),
      isNew: this.toBoolean(this.read(record, ['isNew', 'IsNew'])),
      tFeeCurrency: this.toText(this.read(record, ['tFeeCurrency', 'TFeeCurrency'])),
      rfrTradeId: this.toText(this.read(record, ['rfrTradeId', 'RfrTradeId'])),
      rfrStatus: this.toText(this.read(record, ['rfrStatus', 'RfrStatus'])),
      swapTheoreticalFee: this.toNullableNumber(this.read(record, ['swapTheoreticalFee', 'SwapTheoreticalFee'])),
      genericField1: this.toNullableNumber(this.read(record, ['genericField1', 'GenericField1'])),
      genericField2: this.toNullableNumber(this.read(record, ['genericField2', 'GenericField2'])),
      genericField3: this.toText(this.read(record, ['genericField3', 'GenericField3'])),
      hasTermSheet: this.toBoolean(this.read(record, ['hasTermSheet', 'HasTermSheet'])),
      structureId: this.toText(this.read(record, ['structureId', 'StructureId'])),
      pendingChildren: this.toText(this.read(record, ['pendingChildren', 'PendingChildren'])),
      counterpartyName: this.toText(this.read(record, ['counterpartyName', 'CounterpartyName'])),
      doneUser: this.toText(this.read(record, ['doneUser', 'DoneUser'])),
      securityId: this.toText(this.read(record, ['securityId', 'SecurityId', 'secId', 'SecId'])),
      notional: this.toNullableNumber(this.read(record, ['notional', 'Notional'])),
      dealId: this.toText(this.read(record, ['dealId', 'DealId'])),
      tradeId: this.toText(this.read(record, ['tradeId', 'TradeId'])),
      settlementDate: this.toText(this.read(record, ['settlementDate', 'SettlementDate'])),
      maturityDate: this.toText(this.read(record, ['maturityDate', 'MaturityDate'])),
      rfrEffDateRec: this.toText(this.read(record, ['rfrEffDateRec', 'RfrEffDateRec'])),
      rfrEffDatePay: this.toText(this.read(record, ['rfrEffDatePay', 'RfrEffDatePay'])),
      rfrTradeDate: this.toText(this.read(record, ['rfrTradeDate', 'RfrTradeDate'])),
      tradeDate: this.toText(this.read(record, ['tradeDate', 'TradeDate'])),
      unformattedSettlementDate: this.toText(
        this.read(record, ['unformattedSettlementDate', 'UnformattedSettlementDate']),
      ),
    };
  }

  private toSwapLeg(value: unknown): SwapLegDetail {
    const record = this.toRecord(value);

    return {
      ccy: this.toText(this.read(record, ['ccy', 'Ccy'])),
      notional: this.toNullableNumber(this.read(record, ['notional', 'Notional'])),
      location: this.toText(this.read(record, ['location', 'Location'])),
      company: this.toText(this.read(record, ['company', 'Company'])),
      desk: this.toText(this.read(record, ['desk', 'Desk'])),
      book: this.toText(this.read(record, ['book', 'Book'])),
      folder: this.toText(this.read(record, ['folder', 'Folder'])),
      indexName: this.toText(this.read(record, ['indexName', 'IndexName'])),
      termRateSpread: this.toText(this.read(record, ['termRateSpread', 'TermRateSpread'])),
      rate: this.toText(this.read(record, ['rate', 'Rate'])),
      spread: this.toText(this.read(record, ['spread', 'Spread'])),
      assetFormula: this.toText(this.read(record, ['assetFormula', 'AssetFormula'])),
      rfrFallbackSpread: this.toText(this.read(record, ['rfrFallbackSpread', 'RfrFallbackSpread'])),
      formulas: this.toFormulas(this.read(record, ['formulas', 'Formulas'])),
    };
  }

  private toFormulas(value: unknown): SwapFormulaItem[] {
    if (!Array.isArray(value)) {
      return [];
    }

    return value.map((entry) => {
      const record = this.toRecord(entry);

      return {
        tradeId: this.toText(this.read(record, ['tradeId', 'TradeId'])),
        assetId: this.toText(this.read(record, ['assetId', 'AssetId'])),
        payRec: this.toText(this.read(record, ['payRec', 'PayRec'])),
        applyDate: this.toText(this.read(record, ['applyDate', 'ApplyDate'])),
        formula: this.toText(this.read(record, ['formula', 'Formula'])),
      };
    });
  }

  private toSwapExtLocal(value: unknown): SwapExtLocalDetail {
    const record = this.toRecord(value);

    return {
      tradeId: this.toText(this.read(record, ['tradeId', 'TradeId'])),
      parentId: this.toText(this.read(record, ['parentId', 'ParentId'])),
      dealId: this.toText(this.read(record, ['dealId', 'DealId'])),
      purposeId: this.toNullableNumber(this.read(record, ['purposeId', 'PurposeId'])),
      purposeSubtypeId: this.toNullableNumber(this.read(record, ['purposeSubtypeId', 'PurposeSubtypeId'])),
      modifiedBy: this.toText(this.read(record, ['modifiedBy', 'ModifiedBy'])),
      dateModified: this.toText(this.read(record, ['dateModified', 'DateModified'])),
      comments: this.toText(this.read(record, ['comments', 'Comments'])),
      swapTheoreticalFee: this.toNullableNumber(this.read(record, ['swapTheoreticalFee', 'SwapTheoreticalFee'])),
      tFeeCurrency: this.toText(this.read(record, ['tFeeCurrency', 'TFeeCurrency'])),
      genericField1: this.toNullableNumber(this.read(record, ['genericField1', 'GenericField1'])),
      genericField2: this.toNullableNumber(this.read(record, ['genericField2', 'GenericField2'])),
      genericField3: this.toText(this.read(record, ['genericField3', 'GenericField3'])),
      hasTermSheet: this.toBoolean(this.read(record, ['hasTermSheet', 'HasTermSheet'])),
      purposes: this.toPurposes(this.read(record, ['purposes', 'Purposes'])),
      purposeSubtypes: this.toPurposeSubtypes(this.read(record, ['purposeSubtypes', 'PurposeSubtypes'])),
    };
  }

  private toPurposes(value: unknown): SimpleCatalogOption[] {
    if (!Array.isArray(value)) {
      return [];
    }

    return value.map((entry) => {
      const record = this.toRecord(entry);

      return {
        id: this.toNumber(this.read(record, ['id', 'Id'])),
        name: this.toText(this.read(record, ['name', 'Name'])),
      };
    });
  }

  private toPurposeSubtypes(value: unknown): SwapPurposeSubtypeOption[] {
    if (!Array.isArray(value)) {
      return [];
    }

    return value.map((entry) => {
      const record = this.toRecord(entry);

      return {
        id: this.toNumber(this.read(record, ['id', 'Id'])),
        name: this.toText(this.read(record, ['name', 'Name'])),
        purposeId: this.toNullableNumber(this.read(record, ['purposeId', 'PurposeId'])),
        purposeName: this.toText(this.read(record, ['purposeName', 'PurposeName'])),
      };
    });
  }

  private toSwapActions(value: unknown): SwapActionItem[] {
    if (!Array.isArray(value)) {
      return [];
    }

    return value.map((entry) => this.toSwapAction(entry));
  }

  private toSwapAction(value: unknown): SwapActionItem {
    const record = this.toRecord(value);

    return {
      id: this.toNumber(this.read(record, ['id', 'Id'])),
      tradeId: this.toText(this.read(record, ['tradeId', 'TradeId'])),
      tradeDate: this.toText(this.read(record, ['tradeDate', 'TradeDate'])),
      actionTypeId: this.toNullableNumber(this.read(record, ['actionTypeId', 'ActionTypeId'])),
      actionTypeName: this.toText(this.read(record, ['actionTypeName', 'ActionTypeName'])),
      actionTypeDescription: this.toText(
        this.read(record, ['actionTypeDescription', 'ActionTypeDescription', 'description', 'Description']),
      ),
      comments: this.toText(this.read(record, ['comments', 'Comments'])),
      effectiveDate: this.toText(this.read(record, ['effectiveDate', 'EffectiveDate'])),
    };
  }

  private toSwapActionTypes(value: unknown): SwapActionTypeOption[] {
    if (!Array.isArray(value)) {
      return [];
    }

    return value.map((entry) => {
      const record = this.toRecord(entry);
      return {
        id: this.toNumber(this.read(record, ['id', 'Id'])),
        name: this.toText(this.read(record, ['name', 'Name'])),
        description: this.toText(this.read(record, ['description', 'Description'])),
      };
    });
  }

  private toSwapFees(value: unknown): SwapFeeItem[] {
    if (!Array.isArray(value)) {
      return [];
    }

    return value.map((entry) => this.toSwapFee(entry));
  }

  private toSwapFee(value: unknown): SwapFeeItem {
    const record = this.toRecord(value);

    return {
      id: this.toNumber(this.read(record, ['id', 'Id'])),
      tradeId: this.toText(this.read(record, ['tradeId', 'TradeId'])),
      feeBpsId: this.toNullableNumber(this.read(record, ['feeBpsId', 'FeeBpsId'])),
      value: this.toNullableNumber(this.read(record, ['value', 'Value'])),
      feeTypeName: this.toText(this.read(record, ['feeTypeName', 'FeeTypeName'])),
    };
  }

  private toSwapFeeTypes(value: unknown): SwapFeeTypeOption[] {
    if (!Array.isArray(value)) {
      return [];
    }

    return value.map((entry) => {
      const record = this.toRecord(entry);

      return {
        id: this.toNumber(this.read(record, ['id', 'Id'])),
        name: this.toText(this.read(record, ['name', 'Name'])),
        bps: this.toNullableNumber(this.read(record, ['bps', 'Bps'])),
      };
    });
  }

  private toDocuments(value: unknown): SwapDocumentItem[] {
    if (!Array.isArray(value)) {
      return [];
    }

    return value.map((entry) => this.toDocument(this.toRecord(entry)));
  }

  private normalizeDocuments(response: unknown): SwapDocumentItem[] {
    if (Array.isArray(response)) {
      return this.toDocuments(response);
    }

    const record = this.toRecord(response);
    const data = record['data'];
    if (Array.isArray(data)) {
      return this.toDocuments(data);
    }

    return [];
  }

  private toDocument(record: Record<string, unknown>): SwapDocumentItem {
    return {
      id: this.toNumber(this.read(record, ['id', 'Id', 'documentId', 'DocumentId'])),
      parentElementId: this.toText(this.read(record, ['parentElementId', 'ParentElementId'])),
      securityId: this.toText(this.read(record, ['securityId', 'SecurityId'])),
      description: this.toText(this.read(record, ['description', 'Description', 'name', 'Name', 'documentName', 'DocumentName'])),
      type: this.toText(this.read(record, ['type', 'Type', 'documentType', 'DocumentType'])),
      filename: this.toText(this.read(record, ['filename', 'Filename', 'fileName', 'FileName'])),
      ezShareId: this.toText(this.read(record, ['ezShareId', 'EzShareId', 'eZShareId', 'EZShareId'])),
      ezShareLink: this.toText(this.read(record, ['ezShareLink', 'EzShareLink', 'eZShareLink', 'EZShareLink'])),
      source: this.toText(this.read(record, ['source', 'Source'])),
    };
  }

  private toNotes(value: unknown): SwapNoteItem[] {
    if (!Array.isArray(value)) {
      return [];
    }

    return value.map((entry) => {
      const record = this.toRecord(entry);

      return {
        id: this.toNumber(this.read(record, ['id', 'Id'])),
        parentId: this.toText(this.read(record, ['parentId', 'ParentId'])),
        source: this.toText(this.read(record, ['source', 'Source'])),
        description: this.toText(this.read(record, ['description', 'Description'])),
        modifiedBy: this.toText(this.read(record, ['modifiedBy', 'ModifiedBy'])),
        dateModified: this.toText(this.read(record, ['dateModified', 'DateModified'])),
      };
    });
  }

  private normalizeNotes(response: unknown): SwapNoteItem[] {
    if (Array.isArray(response)) {
      return this.toNotes(response);
    }

    const record = this.toRecord(response);
    const data = record['data'];
    if (Array.isArray(data)) {
      return this.toNotes(data);
    }

    return [];
  }

  private normalizeNote(response: unknown): SwapNoteItem {
    const record = this.toRecord(response);
    const data = this.toRecord(record['data']);
    return Object.keys(data).length > 0
      ? this.toNote(data)
      : this.toNote(record);
  }

  private toNote(value: unknown): SwapNoteItem {
    const record = this.toRecord(value);

    return {
      id: this.toNumber(this.read(record, ['id', 'Id'])),
      parentId: this.toText(this.read(record, ['parentId', 'ParentId'])),
      source: this.toText(this.read(record, ['source', 'Source'])),
      description: this.toText(this.read(record, ['description', 'Description'])),
      modifiedBy: this.toText(this.read(record, ['modifiedBy', 'ModifiedBy'])),
      dateModified: this.toText(this.read(record, ['dateModified', 'DateModified'])),
    };
  }

  private toRecord(value: unknown): Record<string, unknown> {
    if (value && typeof value === 'object') {
      return value as Record<string, unknown>;
    }

    return {};
  }

  private extractResponseRecord(response: unknown): Record<string, unknown> {
    const record = this.toRecord(response);
    const data = this.toRecord(record['data']);

    return Object.keys(data).length > 0 ? data : record;
  }

  private read(record: Record<string, unknown>, keys: string[]): unknown {
    for (const key of keys) {
      if (record[key] !== undefined) {
        return record[key];
      }
    }

    return undefined;
  }

  private toText(value: unknown): string {
    return typeof value === 'string' ? value.trim() : '';
  }

  private toNumber(value: unknown): number {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }

    if (typeof value === 'string') {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : 0;
    }

    return 0;
  }

  private toNullableNumber(value: unknown): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }

    if (typeof value === 'string') {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : null;
    }

    return null;
  }

  private toBoolean(value: unknown): boolean {
    if (typeof value === 'boolean') {
      return value;
    }

    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      return normalized === 'true' || normalized === '1' || normalized === 'yes';
    }

    if (typeof value === 'number') {
      return value === 1;
    }

    return false;
  }
}
